<template>
  <v-card data-testid="assessments-list-container">
    <!-- Show loading state during initialization -->
    <v-row v-if="!auth.isClientHydrated.value" justify="center" class="py-8">
      <v-col cols="auto" class="text-center">
        <v-progress-circular
          indeterminate
          size="64"
          data-testid="assessments-loading-spinner"
        />
        <div class="mt-4 text-medium-emphasis">Loading user information...</div>
      </v-col>
    </v-row>

    <!-- Error state -->
    <v-row v-else-if="error" justify="center" class="py-8">
      <v-col cols="auto" class="text-center">
        <v-icon size="64" color="error" class="mb-4">mdi-alert-circle</v-icon>
        <div class="text-h6 text-error mb-2" data-testid="assessments-error-title">Access Error</div>
        <div class="text-medium-emphasis" data-testid="assessments-error-message">{{ error }}</div>
      </v-col>
    </v-row>

    <!-- Main content when user is loaded -->
    <template v-else>
      <v-card-text>
        <!-- Show message when no assessments are available at all -->
        <div v-if="visibleLanguageTabs.length === 0" class="text-center py-8" data-testid="assessments-no-content">
          <v-icon size="64" color="medium-emphasis" class="mb-4">mdi-clipboard-text-off</v-icon>
          <div class="text-h6 text-medium-emphasis mb-2">No assessments available</div>
          <div class="text-body-2 text-medium-emphasis">There are currently no assessments available for your account.</div>
        </div>

        <!-- Show tabs and content when assessments are available -->
        <template v-else>
          <v-tabs
            v-model="activeLanguageTab"
            align-tabs="start"
            class="mb-6"
            data-testid="assessments-language-tabs"
          >
            <v-tab
              v-for="languageTab in visibleLanguageTabs"
              :key="languageTab.value"
              :value="languageTab.value"
              :data-testid="`assessments-language-${languageTab.value}-tab`"
            >
              {{ languageTab.text }}
              <v-chip
                v-if="getAssessmentCountForLanguage(languageTab.value) > 0"
                size="x-small"
                variant="outlined"
                class="ml-2"
                :data-testid="`assessments-language-${languageTab.value}-count`"
              >
                {{ getAssessmentCountForLanguage(languageTab.value) }}
              </v-chip>
            </v-tab>
          </v-tabs>

          <v-window v-model="activeLanguageTab" class="mt-4" data-testid="assessments-content-window">
            <v-window-item
              v-for="languageTab in visibleLanguageTabs"
              :key="languageTab.value"
              class="pa-1"
              :value="languageTab.value"
              :data-testid="`assessments-${languageTab.value}-content`"
            >
              <v-row v-if="loading" justify="center" class="py-8">
                <v-col cols="auto" class="text-center">
                  <v-progress-circular
                    indeterminate
                    size="64"
                    data-testid="assessments-language-loading-spinner"
                  />
                  <div class="mt-4 text-medium-emphasis">Loading assessments...</div>
                </v-col>
              </v-row>

              <v-row v-else-if="getAssessmentsForLanguage(languageTab.value).length === 0" justify="center" class="py-8">
                <v-col cols="auto" class="text-center">
                  <v-icon size="64" color="medium-emphasis" class="mb-4">mdi-clipboard-text-off</v-icon>
                  <div class="text-body-2 text-medium-emphasis" :data-testid="`assessments-${languageTab.value}-empty`">There are no assessments available in {{ languageTab.text }}.</div>
                </v-col>
              </v-row>

              <v-row v-else>
                <v-col
                  v-for="assessment in getAssessmentsForLanguage(languageTab.value)"
                  :key="assessment.id"
                  cols="12"
                  md="6"
                  lg="4"
                >
                  <v-card
                    hover
                    class="cursor-pointer assessment-card"
                    elevation="2"
                    :class="{ 'draft-assessment': assessment.isDraft }"
                    :data-testid="`assessment-${assessment.id}-card`"
                    @click="startAssessment(assessment)"
                  >
                    <v-card-title class="d-flex align-center" :data-testid="`assessment-${assessment.id}-title`">
                      {{ assessment.name }}
                      <v-chip
                        v-if="assessment.isDraft"
                        size="small"
                        color="warning"
                        variant="flat"
                        class="ml-2"
                        :data-testid="`assessment-${assessment.id}-draft-badge`"
                      >
                        DRAFT
                      </v-chip>
                    </v-card-title>

                    <v-card-text>
                      <v-list density="compact" class="pa-0">
                        <v-list-item class="px-0">
                          <template #prepend>
                            <v-icon size="18">mdi-clock-outline</v-icon>
                          </template>
                          <v-list-item-title class="text-caption font-weight-medium">Time Limit:</v-list-item-title>
                          <template #append>
                            <span class="text-body-2 font-weight-bold" :data-testid="`assessment-${assessment.id}-time-limit`">{{ formatTime(assessment.timeSec) }}</span>
                          </template>
                        </v-list-item>

                        <v-list-item class="px-0">
                          <template #prepend>
                            <v-icon size="18">mdi-help-circle-outline</v-icon>
                          </template>
                          <v-list-item-title class="text-caption font-weight-medium">Total Marks:</v-list-item-title>
                          <template #append>
                            <span class="text-body-2 font-weight-bold" :data-testid="`assessment-${assessment.id}-total-marks`">{{ assessment.marks }} marks</span>
                          </template>
                        </v-list-item>

                        <v-list-item class="px-0">
                          <template #prepend>
                            <v-icon size="18">mdi-target</v-icon>
                          </template>
                          <v-list-item-title class="text-caption font-weight-medium">Pass Mark:</v-list-item-title>
                          <template #append>
                            <span class="text-body-2 font-weight-bold" :data-testid="`assessment-${assessment.id}-pass-mark`">{{ assessment.pass }}%</span>
                          </template>
                        </v-list-item>

                        <v-list-item class="px-0">
                          <template #prepend>
                            <v-icon size="18">mdi-star-outline</v-icon>
                          </template>
                          <v-list-item-title class="text-caption font-weight-medium">Marking:</v-list-item-title>
                          <template #append>
                            <div class="d-flex align-center">
                              <span class="text-body-2 font-weight-bold mr-1" :data-testid="`assessment-${assessment.id}-marking-strategy`">{{ getStrategyName(assessment.marking) }}</span>
                              <MarkingStrategyTooltip :strategy="assessment.marking" />
                            </div>
                          </template>
                        </v-list-item>
                      </v-list>
                    </v-card-text>

                    <v-card-actions>
                      <v-spacer />
                      <v-btn
                        prepend-icon="mdi-play"
                        :data-testid="`assessment-${assessment.id}-start-button`"
                        @click.stop="startAssessment(assessment)"
                      >Ready to Start</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </v-window-item>
          </v-window>
        </template>
      </v-card-text>
    </template>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import MarkingStrategyTooltip from '~/components/MarkingStrategyTooltip.vue';
import { get } from '~/composables/useApi';
import { useAuth } from '~/composables/useAuth';
import { getLanguagesAsArray } from '~/utils/text';
import { getStrategyName } from '~/utils/marking';
import { formatTime } from '~/utils/formatters';
import type { MemoMarkingEnum } from '#shared/enums';

// Use auth composable's hydration state for SSG compatibility

// Type definitions
interface Assessment {
  id: number | string; // Can be numeric for real assessments or 'demo' for demo assessments
  memoId: number; // Always includes the actual memo ID
  name: string;
  enName: string;
  names?: Record<string, string>; // All language names
  timeSec: number | null;
  timeMinutes: number | null;
  pass: number;
  marks: number;
  marking: MemoMarkingEnum; 
  status: string;
  isDraft?: boolean; // Flag to indicate draft assessments
  availableLanguages: string[];
  expire: string | null;
}

// Reactive state
const allAssessments = ref<Assessment[]>([]); // Store all assessments regardless of language
const assessmentsByLanguage = ref<Record<string, Assessment[]>>({}); // Filtered assessments by language
const loading = ref(false);
const error = ref<string | null>(null);
const activeLanguageTab = ref<string>();
const languageLoading = ref<Record<string, boolean>>({});
const loadedLanguages = ref<Set<string>>(new Set());
// Authentication and role checking - computed values
const auth = useAuth();
const user = computed(() => {
  // Only access auth data after client-side hydration
  if (!auth.isClientHydrated.value) return null;
  return auth.getUserData();
});

const userLanguage = computed(() => {
  // Use the user's mother tongue from auth data, no default fallback
  // Handle empty string case properly
  const lang = user.value?.language;
  return (lang && lang !== '') ? lang : null;
});

// Demo user language tabs - only show tabs that have assessments
const visibleLanguageTabs = computed(() => {
  if (!user.value) return [];
  
  // Determine which languages have assessments
  const languagesWithAssessments = new Set<string>();
  
  // Add all languages that have assessments
  Object.keys(assessmentsByLanguage.value).forEach(lang => {
    if ((assessmentsByLanguage.value[lang]?.length ?? 0) > 0) {
      languagesWithAssessments.add(lang);
    }
  });
  
  // For demo users and admin users, show all language tabs that have assessments
  if (user.value?.role === 'DEMO' || user.value?.role === 'ADMIN') {
    return getLanguagesAsArray().filter(lang => 
      lang.value && languagesWithAssessments.has(lang.value)
    );
  }
  
  // For students, show language tabs that have assessments, but always include user's mother tongue
  if (userLanguage.value && !languagesWithAssessments.has(userLanguage.value)) {
    // If user's language has no assessments, still show it (will display "no assessments" message)
    languagesWithAssessments.add(userLanguage.value);
  }
  
  return getLanguagesAsArray().filter(lang => 
    lang.value && languagesWithAssessments.has(lang.value)
  );
});

// Assessment helpers
function getAssessmentsForLanguage(language: string): Assessment[] {
  return assessmentsByLanguage.value[language] || [];
}

function getAssessmentCountForLanguage(language: string): number {
  return getAssessmentsForLanguage(language).length;
}

// Initialize by fetching all assessments at once
// Guard to prevent multiple initializations
let isInitialized = false;

async function initializeLanguages() {
  if (isInitialized) {
    // Already initialized, skip duplicate calls
    return;
  }
  
  isInitialized = true;
  // Initialize assessments data
  loading.value = true;
  error.value = null;
  
  try {
    // Fetch all assessments without language filter to get everything at once
    const endpoint = 'assessments';
    // Fetch all assessments (DEMO/DRAFT assessments don't have outcomes)
    const response = await get(`${endpoint}`);
    
    if (response && Array.isArray(response)) {
      // Store all assessments
      allAssessments.value = response;
      
      // Organize assessments by language
      const languages = getLanguagesAsArray().filter(lang => lang.value).map(lang => lang.value);
      
      // Initialize empty arrays for all languages
      languages.forEach(language => {
        assessmentsByLanguage.value[language] = [];
        languageLoading.value[language] = false;
      });
      
      // Filter assessments by available languages and deduplicate by name within each language
      allAssessments.value.forEach(assessment => {
        if (assessment.availableLanguages && Array.isArray(assessment.availableLanguages)) {
          assessment.availableLanguages.forEach(lang => {
            if (!assessmentsByLanguage.value[lang]) {
              assessmentsByLanguage.value[lang] = [];
            }
            
            // Create a copy with the correct name for this language
            const localizedAssessment = {
              ...assessment,
              name: assessment.names && assessment.names[lang] 
                ? assessment.names[lang] 
                : (assessment.name || `Assessment ${assessment.id}`)
            };
            
            // Check if an assessment with this name already exists in this language
            const existingIndex = assessmentsByLanguage.value[lang].findIndex(
              existing => existing.name === localizedAssessment.name
            );
            
            if (existingIndex === -1) {
              // No duplicate found, add the assessment
              assessmentsByLanguage.value[lang].push(localizedAssessment);
            } else {
              // Duplicate found, keep the one with more complete data (prefer the one with names object)
              const existing = assessmentsByLanguage.value[lang][existingIndex];
              if (localizedAssessment.names && Object.keys(localizedAssessment.names).length > (existing?.names ? Object.keys(existing.names).length : 0)) {
                // Replace with the more complete assessment
                assessmentsByLanguage.value[lang][existingIndex] = localizedAssessment;
              }
              // Otherwise keep the existing one
            }
          });
        }
      });
      
      // Mark all languages as loaded
      languages.forEach(language => {
        loadedLanguages.value.add(language);
      });
    } else {
      // No assessments available
      allAssessments.value = [];
    }

    // Set default active tab to user's mother tongue, or first available language
    // First check which languages have assessments
    const languagesWithAssessments = new Set<string>();
    Object.keys(assessmentsByLanguage.value).forEach(lang => {
      if ((assessmentsByLanguage.value[lang]?.length ?? 0) > 0) {
        languagesWithAssessments.add(lang);
      }
    });

    if (userLanguage.value && languagesWithAssessments.has(userLanguage.value)) {
      activeLanguageTab.value = userLanguage.value;
    } else if (languagesWithAssessments.size > 0) {
      // Use the first available language if user language not available
      activeLanguageTab.value = Array.from(languagesWithAssessments)[0];
    }
    
  } catch (err) {
    console.error('Error initializing assessments:', err);
    error.value = 'Failed to load assessments. Please try again later.';
  } finally {
    loading.value = false;
  }
}


// Start an assessment
async function startAssessment(assessment: Assessment) {
  try {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      console.warn('sessionStorage not available in SSR context');
      return;
    }
    
    // Store the memo data in sessionStorage for the instructions page
    const memoData = {
      id: assessment.id,
      memoId: assessment.memoId, // Always store the memo ID for API calls
      name: assessment.name,
      enName: assessment.name,
      timeSec: assessment.timeSec,
      timeMinutes: assessment.timeMinutes,
      pass: assessment.pass,
      marks: assessment.marks,
      marking: assessment.marking,
      status: assessment.status,
      isDraft: assessment.isDraft, // Pass draft flag
      availableLanguages: assessment.availableLanguages,
      expire: assessment.expire,
      // Add current language for the assessment
      selectedLanguage: activeLanguageTab.value
    };
    
    sessionStorage.setItem(`memo_${assessment.id}`, JSON.stringify(memoData));

    // FAIL LOUDLY if no language selected
    if (!activeLanguageTab.value) {
      console.error('[ASSESSMENTS] No language selected', {
        assessmentId: assessment.id,
        availableLanguages: assessment.availableLanguages
      });
      useToaster().error('Please select a language before starting the assessment');
      return;
    }

    // Navigate to the instructions page with language parameter
    await navigateTo({
      path: `/assessments/${assessment.id}/instructions`,
      query: { language: activeLanguageTab.value }
    });
  } catch (error) {
    console.error('Error starting assessment:', error);
  }
}

// Watch for language tab changes - no need to fetch as all assessments are already loaded
watch(activeLanguageTab, (_newLanguage) => {
  // All assessments are already loaded, just switch tabs
  // The filtering happens automatically through the computed getAssessmentsForLanguage function
});

// Initialize after client-side hydration
onMounted(async () => {
  // Component mounted, initialize assessments
  // Initialize auth hydration first
  auth.hydrate();
  
  // Wait for the next tick to allow reactivity to update
  await nextTick();
  
  // Now check authentication and initialize
  if (auth.isAuthenticated()) {
    const userData = auth.getUserData();
    if (userData && userData.id > 0) {
      if (userData.role === 'DEMO' || userData.role === 'STUDENT' || userData.role === 'ADMIN') {
        await initializeLanguages();
      } else {
        error.value = 'Access denied: You do not have permission to view this page.';
      }
    } else {
      error.value = 'Please log in to view this page.';
    }
  } else {
    error.value = 'Please log in to view this page.';
  }
});
</script>

